import { useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PermissionDenied } from '@/components/ui/permission-denied';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { STATE_GROUPS } from '../constants';
import { useCanAdminProjects } from '../permissions';
import { statesHooks, workItemTypesHooks } from '../queries';
import type { State, StateGroup, WorkItemType } from '../types';

const TEMPLATE = { template: 1 };

function rows<T>(raw: { results: T[] } | T[] | undefined): T[] {
  return (Array.isArray(raw) ? raw : (raw?.results ?? [])) as T[];
}

/** Org-level template states + work-item types (ADR-071). Editing here changes
 * the defaults *new* projects are seeded with; existing projects keep their own
 * copy and override per project under each project's Settings. */
export function OrgProjectDefaultsPage() {
  const canAdmin = useCanAdminProjects();
  if (!canAdmin) return <PermissionDenied />;

  return (
    <div className="max-w-3xl space-y-8">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Project defaults</h1>
        <p className="text-muted-foreground text-sm">
          The states and work-item types new projects start with. Existing projects keep their own copy —
          edit those under each project’s Settings.
        </p>
      </div>
      <DefaultStates />
      <DefaultTypes />
    </div>
  );
}

const stateGroupLabel = (g: string) => STATE_GROUPS.find((x) => x.value === g)?.label ?? g;

function DefaultStates() {
  const query = statesHooks.useList(TEMPLATE);
  const create = statesHooks.useCreate();
  const remove = statesHooks.useRemove();
  const [name, setName] = useState('');
  const [group, setGroup] = useState<StateGroup>('unstarted');
  const [color, setColor] = useState('#64748b');

  const states = useMemo(
    () => rows<State>(query.data).sort((a, b) => a.order - b.order),
    [query.data],
  );

  return (
    <section className="space-y-3">
      <h2 className="text-sm font-medium">Default states</h2>
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 space-y-1">
          <Label className="text-xs">Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="In Review" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Group</Label>
          <Select value={group} onValueChange={(v) => setGroup(v as StateGroup)}>
            <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATE_GROUPS.map((g) => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Color</Label>
          <Input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="h-9 w-14 p-1" />
        </div>
        <Button
          onClick={() => {
            if (name.trim()) {
              create.mutate({ name: name.trim(), group, color, order: states.length });
              setName('');
            }
          }}
          disabled={!name.trim() || create.isPending}
        >
          <Plus className="size-4" /> Add
        </Button>
      </div>
      {query.isPending ? (
        <Skeleton className="h-24 w-full" />
      ) : (
        <ul className="space-y-2">
          {states.map((s) => (
            <li key={s.id} className="flex items-center gap-3 rounded-md border px-3 py-2">
              <span className="size-3 rounded-full" style={{ backgroundColor: s.color || '#94a3b8' }} />
              <span className="flex-1 text-sm">{s.name}</span>
              <Badge variant="outline" className="text-xs">{stateGroupLabel(s.group)}</Badge>
              {s.is_default ? <Badge className="text-xs">default</Badge> : null}
              <Button
                variant="ghost" size="icon"
                className="text-muted-foreground hover:text-destructive"
                onClick={() => remove.mutate(s.id)}
                aria-label={`Delete ${s.name}`}
              >
                <Trash2 className="size-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function DefaultTypes() {
  const query = workItemTypesHooks.useList(TEMPLATE);
  const create = workItemTypesHooks.useCreate();
  const remove = workItemTypesHooks.useRemove();
  const [name, setName] = useState('');
  const types = useMemo(() => rows<WorkItemType>(query.data), [query.data]);

  return (
    <section className="space-y-3">
      <h2 className="text-sm font-medium">Default work-item types</h2>
      <div className="flex gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="New type (e.g. Spike)" />
        <Button
          onClick={() => {
            if (name.trim()) {
              create.mutate({ name: name.trim(), order: types.length });
              setName('');
            }
          }}
          disabled={!name.trim() || create.isPending}
        >
          <Plus className="size-4" /> Add
        </Button>
      </div>
      {query.isPending ? (
        <Skeleton className="h-24 w-full" />
      ) : (
        <ul className="space-y-2">
          {types.map((t) => (
            <li key={t.id} className="flex items-center gap-3 rounded-md border px-3 py-2">
              <span className="flex-1 text-sm">{t.name}</span>
              {t.is_epic ? <Badge variant="outline" className="text-xs">epic</Badge> : null}
              {t.is_default ? <Badge className="text-xs">default</Badge> : null}
              <Button
                variant="ghost" size="icon"
                className="text-muted-foreground hover:text-destructive"
                onClick={() => remove.mutate(t.id)}
                aria-label={`Delete ${t.name}`}
              >
                <Trash2 className="size-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
